import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import { VBar } from '../../vbar/VBar'
import type { VBarStackClusterProps } from '../types'
import type { ClusterItemMetadata } from './VBarStackAPI'
import classes from './VBar.module.scss'
import { defaultVBarFontMetrics, VBarFontMetricsContext } from '../../vbar/VBarFontMetricsContext'

export type VBarClusterProps = VBarStackClusterProps<ClusterItemMetadata>

/**
 * This component renders a VBar cluster. This is a placeholder for VBars that are grouped together.
 * On hover, this item will be removed and replaced by the individual VBar items.
 *
 * This cluster component uses the VBar props from the its first item to style the cluster.
 */
export const VBarCluster: React.FC<VBarClusterProps> = memo(({ cluster }) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // We delay (hover:true) events to avoid opening the cluster too quickly when the user hovers past the cluster.
  // On (hover:false) events, we clear the delayed onExpand call.
  const onHover = useCallback(
    (hovered: boolean) => {
      if (hovered && !timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          cluster.onExpand()
        }, 100)
      } else if (!hovered && timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    },
    [cluster],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  const fontMetricsContext = useContext(VBarFontMetricsContext)
  const lineHeight =
    cluster.items[0].lineHeight ??
    fontMetricsContext.lineHeight ??
    defaultVBarFontMetrics.lineHeight

  // We know what the primary and secondary text is, and it's unlikely to overflow
  const maxHeight = Math.max(1 * lineHeight, cluster.anchorHeight)

  const onClick = useCallback(() => cluster.onExpand(), [cluster])

  return (
    <div
      className={classes.vBarCluster}
      style={{
        top: cluster.top,
        left: cluster.items[0].left,
        width: cluster.items[0].width,
        height: maxHeight,
        maxHeight,
        zIndex: Math.max(...cluster.items.map(item => item.zIndex ?? 0)),
      }}
    >
      <VBar
        // We use the props from the first item to style the cluster.
        // NOTE: this is arbitrary, we can have a more complex logic to determine the cluster style.
        {...cluster.items[0]}
        // In practice, there should be 2+ items
        primaryText={`${cluster.items.length} suggestion${cluster.items.length > 1 ? 's' : ''}`}
        secondaryText=""
        onHover={onHover}
        onClick={onClick}
        anchorHeight={cluster.anchorHeight}
      />
    </div>
  )
})
